import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit,
         ViewChild
       } from '@angular/core';
import { SVG, Svg, Image as SvgImage } from '@svgdotjs/svg.js';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { ImagePlaceholder
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { TransformationManager
       } from 'collage-designer/stage/placeholder/transformation-manager';
import { CropArea } from 'core/crop-area';
import { clamp, ConvexArea, MatrixLike, Point, Region, RegionLike
       } from 'core/geometry';
import { Utils } from 'core/utils';
import { Subscription } from 'rxjs';

import { injectMultiversionImageBehavior
       } from '../multiversion-image/multiversion-image';

import { AnimationRequest } from './animation-request';
import { CropGestureHandler, CropGestureSensor } from './crop-gestures';
import { CropHandle } from './crop-handle';
import { RegionScaler } from './region-scaler';
import { pinSVGElement } from './svg-region';
import { ViewTransform } from './view-transform';

interface CropperView
{
  frame: Region;
  transform: ViewTransform;
}

@Component({
    selector: 'image-cropper',
    template: '<div #svgContainer class=\'image-cropper-svg-container\'></div>',
    styleUrls: ['./image-cropper.component.scss'],
    standalone: false
})
export class ImageCropperComponent implements OnInit, OnDestroy
{
  private static readonly CUT_OUT_SHAPE: string = 'M 0 0 h 1 v 1 h -1 v -1';
  private static readonly MAX_CONTAINER_HEIGHT: number = 600;
  private static readonly MIN_CONTAINER_HEIGHT: number = 180;
  private static readonly SELECTION_PADDING: number = 24;
  private static readonly SETTLE_DURATION: number = 320; /* ms */
  private static readonly ZOOM_TOLERANCE: number = 1e-6;

  @ViewChild('svgContainer', {static: true})
  private container: ElementRef;

  private placeholder: ImagePlaceholder = undefined;
  private editableArea: RegionLike = undefined;
  private imageMatrix: MatrixLike = undefined;
  private imageExtent: Region = undefined;
  private appliedSelection: Region = Region.empty();
  private svgDoc: Svg = undefined;
  private backgroundImage: SvgImage;
  private imageLoaded: boolean = false;
  private cropHandle: CropHandle;
  private gestures: CropGestureSensor;
  private viewTransform: ViewTransform = undefined;
  private selectionFrame: Region = undefined;
  private viewport: Region = undefined;
  private cropArea: CropArea = undefined;
  private frameScaler: RegionScaler = undefined;
  private selectionWasResized: boolean = false;
  private containerObserver: MutationObserver = undefined;

  private readonly settling: AnimationRequest = new AnimationRequest();
  private readonly rendering: AnimationRequest = new AnimationRequest();
  private readonly matrixResponse: AnimationRequest = new AnimationRequest();

  private readonly orientationChangeHandler: () => void =
      this.orientationChange.bind(this);
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
      @Inject(PlaceholderService)
      private readonly placeholderService: PlaceholderService,
      @Inject(DesignStepService)
      private readonly designStepService: DesignStepService)
  {}

  public ngOnInit(): void
  {
    const nativeContainer = this.container.nativeElement;
    nativeContainer.ondragstart = () => false;

    this.svgDoc = SVG().addTo(nativeContainer).size("100%", "100%") as Svg;
    this.containerObserver = new MutationObserver(mutations => {
      this.onResize(undefined);
    });
    this.containerObserver.observe(nativeContainer, {attributes: true});

    this.backgroundImage =
        injectMultiversionImageBehavior(this.svgDoc.image(this.imageUrl));

    this.backgroundImage.on('load', () => {
      this.imageLoaded = true;
      this.onResize(undefined);
    });

    this.cropHandle = new CropHandle(
        this.svgDoc,
        this.svgDoc.path(ImageCropperComponent.CUT_OUT_SHAPE));

    this.gestures = new CropGestureSensor(this.svgDoc, this.gestureHandler);

    this.subscriptions.add(
        this.placeholderService.imageBeingEdited.subscribe(
            (placeholder) => this.adoptPlaceholder(placeholder)));

    window.addEventListener(
        'orientationchange', this.orientationChangeHandler);
  }

  private adoptPlaceholder(placeholder: ImagePlaceholder): void
  {
    if (placeholder !== this.placeholder) {
      this.configureFor(placeholder);
    }

    this.adoptImageMatrix(placeholder.transformationManager);
  }

  private configureFor(placeholder: ImagePlaceholder): void
  {
    const dimensions = placeholder.getDimensions();

    if (dimensions === undefined) {
      return;
    }

    this.placeholder = placeholder;

    this.imageExtent =
        new Region(0, 0, dimensions.width, dimensions.height);
    this.appliedSelection =
        Region.fromSimilar(placeholder.getDisplayedBoundingBox());
    this.editableArea =
        this.designStepService.getDesignStepConfig()
            .imageCropperSettings.showEditableAreaMargins
                ? placeholder.approximateEditableArea()
                : undefined;


    this.changeBackgroundImage();
    this.onResize(undefined, true);
  }

  private adoptImageMatrix(manager: TransformationManager): void
  {
    const matrix = manager.getMatrix();

    if (matrix === this.imageMatrix) {
      return;
    }

    this.imageMatrix = matrix;
    if (! this.isConfigured) {
      return;
    }

    this.matrixResponse.schedule(() => this.fitSelectionToImageMatrix());
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    this.gestures.destructor();
    this.settling.cancel();
    this.rendering.cancel();
    this.matrixResponse.cancel();

    if (this.containerObserver !== undefined) {
      this.containerObserver.disconnect();
    }

    window.removeEventListener(
        'orientationchange', this.orientationChangeHandler);
  }

  private orientationChange(): void
  {
    this.onResize(undefined);
  }

  private get imageUrl(): string
  {
    return this.placeholder === undefined ? '' : this.placeholder.getHref();
  }

  private changeBackgroundImage(): void
  {
    this.backgroundImage.load(this.imageUrl);
  }

  private tryFitImage(): void
  {
    const aspectRatio = this.selectionAspectRatio;

    if (!isFinite(aspectRatio) || aspectRatio <= 0) {
      return;
    }

    const available = this.availableWidth;

    const height =
        Math.min(available / aspectRatio, this.availableHeight);

    this.resizeContainer(
        Math.min(available, height * aspectRatio), height);
  }

  private get hasAppliedSelection(): boolean
  {
    return this.appliedSelection.width > 0 && this.appliedSelection.height > 0;
  }

  private get selectionAspectRatio(): number
  {
    const selection =
        this.hasAppliedSelection
            ? this.appliedSelection
            : new CropArea(this.imageExtent, this.imageMatrix).bounds;

    return selection.width / selection.height;
  }

  private get availableHeight(): number
  {
    const hostElement =
        this.container.nativeElement.parentElement as HTMLDivElement;

    const contentColumn =
        hostElement ? hostElement.parentElement : undefined;

    if (!contentColumn) {
      return ImageCropperComponent.MIN_CONTAINER_HEIGHT;
    }

    let leftover = contentColumn.clientHeight;

    for (const child of Array.from(contentColumn.children)) {
      if (child !== hostElement) {
        leftover -= (child as HTMLElement).offsetHeight;
      }
    }

    return clamp(
        leftover,
        ImageCropperComponent.MIN_CONTAINER_HEIGHT,
        ImageCropperComponent.MAX_CONTAINER_HEIGHT);
  }

  private get availableWidth(): number
  {
    const divContainer = this.container.nativeElement as HTMLDivElement;
    const host = divContainer.parentElement;

    if (!host) {
      return divContainer.getBoundingClientRect().width;
    }

    const style = window.getComputedStyle(host);

    return host.clientWidth
        - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  }

  private resizeContainer(width: number, height: number): void
  {
    const style = (this.container.nativeElement as HTMLDivElement).style;
    const asPixels = (value: number) => `${value.toFixed(3)}px`;

    if (style.width !== asPixels(width)) {
      style.width = asPixels(width);
    }

    if (style.height !== asPixels(height)) {
      style.height = asPixels(height);
    }
  }

  @HostListener('window:resize') public onResize(ev, force = false): void
  {
    if (!this.imageLoaded || this.imageExtent === undefined) {
      return;
    }

    this.tryFitImage();

    const viewport = this.viewportRegion;

    if (viewport.width === 0 || viewport.height === 0) {
      return;
    }

    if (force || !viewport.equals(this.viewport)) {
      this.reconfigure(viewport);
      this.backgroundImage.load(this.imageUrl);
    }
  }

  private get viewportRegion(): Region
  {
    const bounds = this.svgDoc.node.getBoundingClientRect();

    return new Region(0, 0, bounds.width, bounds.height);
  }

  private get selectionBounds(): ConvexArea
  {
    return ConvexArea.fromPolygon(
        this.cropArea.corners.map(
            (corner) => this.viewTransform.pointToViewportSpace(corner)));
  }

  private reconfigure(viewport: Region): void
  {
    this.settling.cancel();

    this.viewport = viewport;
    this.cropArea = new CropArea(this.imageExtent, this.imageMatrix);

    this.layOutSelection(this.initialSelection());

    pinSVGElement(this.backgroundImage, this.imageExtent);
    this.cropHandle.reconfigure(this.editableArea);
    this.render();
  }

  private initialSelection(): Region
  {
    if (this.hasAppliedSelection) {
      return this.cropArea.clampSelection(this.appliedSelection);
    }

    const bounds = this.cropArea.bounds;

    return this.cropArea.largestSelection(bounds.width / bounds.height);
  }

  private viewFitting(selection: Region, share: number): CropperView
  {
    const room =
        Region.centeredFit(this.viewport, selection.width / selection.height);
    const frame =
        new Region(0, 0, room.width * share, room.height * share)
            .centeredAt(room.center);

    return {frame, transform: ViewTransform.fitting(selection, frame)};
  }

  private layOutSelection(selection: Region): void
  {
    const view = this.viewFitting(selection, this.shareFor(selection));

    this.selectionFrame = view.frame;
    this.viewTransform = view.transform;
  }

  private shareFor(selection: Region): number
  {
    const largest =
        this.cropArea.largestSelection(selection.width / selection.height);

    const padded = Math.max(
        0.5,
        1 - 2 * ImageCropperComponent.SELECTION_PADDING
                / Math.min(this.viewport.width, this.viewport.height));

    return Math.max(padded, Math.min(1, selection.width / largest.width));
  }

  private get gestureHandler(): CropGestureHandler
  {
    return {
      beginGesture: (node: Element) => this.beginGesture(node),
      moveBy: (delta: Point, center: Point, factor: number) =>
          this.moveBy(delta, center, factor),
      resizeBy: (offset: Point) => this.resizeSelection(offset),
      endGesture: () => this.settle(),
    };
  }

  private get isConfigured(): boolean
  {
    return this.viewTransform !== undefined;
  }

  private beginGesture(node: Element): boolean
  {
    this.settling.cancel();
    this.selectionWasResized = false;

    const corner =
        this.isConfigured ? this.cropHandle.findCorner(node) : undefined;
    if (corner === undefined) {
      this.markPanning(true);
      return false; // the gesture pans the image
    }

    this.frameScaler = new RegionScaler(
        this.selectionFrame,
        this.smallestFrame(corner.sideLength * 2),
        this.selectionBounds,
        corner.position);

    return true;
  }

  private smallestFrame(handleSize: number): Region
  {
    const smallest = this.cropArea.smallestSelection(
        this.selectionFrame.width / this.selectionFrame.height);
    const scale = this.viewTransform.scale;

    return new Region(
        0,
        0,
        Math.max(handleSize, smallest.width * scale),
        Math.max(handleSize, smallest.height * scale));
  }

  private moveBy(delta: Point, center: Point, factor: number): void
  {
    if (! this.isConfigured) {
      return;
    }

    const selection = this.selection;
    const zoom = this.limitedZoom(selection, factor);
    const shift = new Point(
        -delta.x / this.viewTransform.scale,
        -delta.y / this.viewTransform.scale);

    if (zoom === 1 && shift.x === 0 && shift.y === 0) {
      return;
    }

    this.settling.cancel();

    const anchor = this.viewTransform.pointToTargetSpace(center);
    const zoomed = new Region(
        anchor.x + (selection.x - anchor.x) / zoom,
        anchor.y + (selection.y - anchor.y) / zoom,
        selection.width / zoom,
        selection.height / zoom);

    this.layOutSelection(
        this.cropArea.clampSelection(
            zoomed.centeredAt(zoomed.center.add(shift))));

    this.scheduleRender();
    this.reportSelectionWhileDragging();
  }

  private limitedZoom(selection: Region, factor: number): number
  {
    const zoomedOut =
        selection.width
            / this.cropArea.largestSelection(
                selection.width / selection.height).width;

    const zoom = clamp(factor, zoomedOut, zoomedOut * CropArea.MAX_ZOOM);

    return Math.abs(zoom - 1) > ImageCropperComponent.ZOOM_TOLERANCE
        ? zoom
        : 1;
  }

  private resizeSelection(offset: Point): void
  {
    if (this.frameScaler === undefined) {
      return;
    }

    this.selectionFrame = this.frameScaler.applyScalePoint(
        this.selectionFrame, this.frameScaler.samePoint.add(offset));

    this.selectionWasResized = true;

    this.scheduleRender();
    this.reportSelectionWhileDragging();
  }

  private settle(): void
  {
    this.markPanning(false);

    if (! this.isConfigured) {
      return;
    }

    const selection = this.selection;

    if (this.selectionWasResized) {
      this.selectionWasResized = false;
      this.settleTo(selection);
    }

    this.applySelection(selection);
  }

  private settleTo(selection: Region): void
  {
    const from = this.selectionFrame;
    const to = this.viewFitting(selection, this.shareFor(selection)).frame;
    const startedAt = performance.now();

    const step = (now: number) => {
      const elapsed =
          (now - startedAt) / ImageCropperComponent.SETTLE_DURATION;
      const progress = elapsed < 1 ? 1 - Math.pow(1 - elapsed, 3) : 1;

      this.selectionFrame = Region.interpolate(from, to, progress);
      this.viewTransform =
          ViewTransform.fitting(selection, this.selectionFrame);
      this.render();

      if (progress < 1) {
        this.settling.schedule(step);
      }
    };

    this.settling.cancel();
    this.settling.schedule(step);
  }

  private get selection(): Region
  {
    return this.cropArea.clampSelection(
        this.viewTransform.regionToTargetSpace(this.selectionFrame));
  }

  private reportSelectionWhileDragging(): void
  {
    if (Utils.isLargeDevice()) {
      this.applySelection(this.selection);
    }
  }

  private applySelection(selection: Region): void
  {
    this.appliedSelection = selection;

    if (this.placeholder !== undefined) {
      this.placeholder.applyBoundingBox(selection);
    }
  }

  private markPanning(active: boolean): void
  {
    (this.container.nativeElement as HTMLElement)
        .classList.toggle('image-cropper-panning', active);
  }

  private fitSelectionToImageMatrix(): void
  {
    const currentSelection =
        this.viewTransform.regionToTargetSpace(this.selectionFrame);

    this.cropArea = new CropArea(this.imageExtent, this.imageMatrix);

    const selection = this.cropArea.clampSelection(currentSelection);

    this.layOutSelection(selection);
    this.render();

    if (! selection.equals(this.appliedSelection)) {
      this.applySelection(selection);
    }
  }

  private render(): void
  {
    this.backgroundImage.transform(
        this.viewTransform.composedWith(this.imageMatrix));

    this.cropHandle.render(this.selectionFrame);
  }

  private scheduleRender(): void
  {
    this.rendering.schedule(() => this.render());
  }
}
